import React from "react";
import { Link } from "react-router-dom";
import aboutIllustration from "../../assets/lenders.webp";
import {
  Users,
  Building2,
  MapPin,
  IndianRupee,
  ArrowRight,
  Play,
  Sparkles,
} from "lucide-react";

const impactStats = [
  {
    id: 1,
    value: "10,000+",
    label: "Partner DSAs",
    icon: <Users size={19} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
  {
    id: 2,
    value: "50+",
    label: "Banks & NBFCs",
    icon: <Building2 size={19} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
  {
    id: 3,
    value: "40+ Cities",
    label: "Pan-India Presence",
    icon: <MapPin size={19} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
  {
    id: 4,
    value: "₹15,000 Cr+",
    label: "Credit Facilitated",
    icon: <IndianRupee size={19} className="text-[var(--hero-green)]" strokeWidth={2.2} />,
  },
];

function AboutHero() {
  const scrollToWhyWeBuiltThis = () => {
    const el = document.getElementById("why-we-built-this");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative overflow-hidden bg-[#f7faf8] pt-5 pb-12 sm:pt-8 sm:pb-16 lg:pt-10 lg:pb-20">
      {/* Soft Ambient Radial Glow Gradients */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_12%_35%,rgba(46,229,162,0.12),transparent_40%),radial-gradient(circle_at_88%_45%,rgba(0,135,81,0.08),transparent_40%)]"
      />

      <div className="section-container relative z-10">
        
        {/* ================= 1. BREADCRUMB ================= */}
        <nav className="flex items-center gap-2 font-jakarta text-[12.5px] sm:text-[13px] text-[#6b7280] mb-5 sm:mb-8">
          <Link
            to="/"
            className="hover:text-[var(--hero-green)] transition-colors"
          >
            Home
          </Link>
          <span className="text-[#9ca3af]">›</span>
          <span className="font-semibold text-[#111827]">
            About Us
          </span>
        </nav>

        {/* ================= 2. MAIN HERO 2-COLUMN GRID ================= */}
        <div className="grid grid-cols-1 items-center gap-8 sm:gap-10 lg:grid-cols-12 lg:gap-10 xl:gap-14">
          
          {/* LEFT COLUMN: Narrative, Headline, Subtitle & Action Buttons */}
          <div className="lg:col-span-7 xl:col-span-7">
            
            {/* Tag Badge */}
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--hero-green-light)] px-3 py-1 font-jakarta text-[11.5px] sm:text-[12px] font-bold text-[var(--hero-green)] border border-[var(--hero-green-border)] shadow-xs mb-3.5 sm:mb-4">
              <Sparkles size={13} className="text-[var(--hero-green)]" />
              <span>ABOUT FINVERSE</span>
            </span>

            {/* Main Headline */}
            <h1 className="font-syne text-[28px] sm:text-[38px] md:text-[46px] lg:text-[54px] xl:text-[60px] font-bold leading-[1.15] tracking-[-0.5px] sm:tracking-[-1.5px] text-[#111827] text-balance">
              Pioneering the Future of <span className="text-[var(--hero-green)]">Loan Distribution.</span>
            </h1>

            {/* Sub-headline */}
            <p className="mt-4 sm:mt-5 max-w-[580px] font-jakarta text-[15px] sm:text-[16.5px] lg:text-[17px] leading-[1.65] text-[#556369]">
              Finverse is India&apos;s premier loan clearance and digital DSA distribution
              infrastructure. We bridge field distributors, borrowers, and institutional
              lenders with radical speed, transparency, and guaranteed weekly payouts.
            </p>

            {/* Action Buttons */}
            <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
              <Link
                to="/for-dsas"
                className="inline-flex h-[48px] sm:h-[50px] items-center justify-center gap-2.5 rounded-full bg-[var(--hero-green)] px-7 sm:px-8 font-jakarta text-[14.5px] sm:text-[15px] font-bold text-white shadow-md transition-all duration-200 hover:-translate-y-0.5 hover:bg-[var(--hero-green-hover)] hover:shadow-lg outline-none focus:outline-none"
              >
                <span>Join Our Network</span>
                <ArrowRight size={16} strokeWidth={2.4} />
              </Link>

              <button
                type="button"
                onClick={scrollToWhyWeBuiltThis}
                className="inline-flex h-[48px] sm:h-[50px] items-center justify-center gap-2 rounded-full border border-[#d6e2db] bg-white px-6 sm:px-7 font-jakarta text-[14.5px] sm:text-[15px] font-bold text-[#111827] shadow-2xs transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#f3f7f5] hover:border-[#cbdad1] outline-none focus:outline-none cursor-pointer"
              >
                <span>Why We Built This</span>
                <Play size={13} className="fill-[#111827] text-[#111827]" />
              </button>
            </div>

          </div>

          {/* RIGHT COLUMN: 3D Illustration Pod */}
          <div className="lg:col-span-5 xl:col-span-5 flex items-center justify-center pt-2 lg:pt-0">
            <div className="group relative w-full overflow-hidden rounded-[26px] sm:rounded-[30px] border border-[#d6e8dd] bg-white p-3 sm:p-4 shadow-[0_20px_50px_rgba(0,135,81,0.08)] transition-all duration-300 hover:shadow-[0_25px_60px_rgba(0,135,81,0.14)]">
              
              {/* Ambient Radial Aura */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-16 -top-16 h-52 w-52 rounded-full bg-[radial-gradient(circle,rgba(46,229,162,0.3),transparent_70%)] blur-2xl"
              />

              {/* Banner Artwork Image */}
              <img
                src={aboutIllustration}
                alt="Finverse Loan Distribution Infrastructure"
                className="relative z-10 w-full rounded-[20px] sm:rounded-[24px] object-cover transition-transform duration-300 group-hover:scale-[1.01]"
                loading="eager"
              />
            </div>
          </div>

        </div>

        {/* ================= 3. BOTTOM 4 IMPACT METRIC CARDS ================= */}
        <div className="mt-10 sm:mt-14 lg:mt-16 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
          {impactStats.map((stat) => (
            <div
              key={stat.id}
              className="group relative flex flex-col justify-between rounded-[18px] sm:rounded-[22px] border border-[#dce8e1] bg-white p-3.5 sm:p-5 shadow-[0_3px_15px_rgba(0,0,0,0.02)] transition-all duration-300 hover:-translate-y-1 hover:border-[var(--hero-green-border)] hover:shadow-[0_12px_26px_rgba(0,135,81,0.08)] min-w-0"
            >
              <div className="min-w-0">
                {/* Icon Container */}
                <div className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 aspect-square items-center justify-center rounded-xl bg-[var(--hero-green-light)] border border-[var(--hero-green-border)] text-[var(--hero-green)] mb-2.5 sm:mb-3 shadow-2xs transition-transform duration-300 group-hover:scale-108">
                  {stat.icon}
                </div>

                {/* Big Metric Value */}
                <p className="font-bricolage text-[19px] sm:text-[24px] lg:text-[26px] font-bold leading-tight tracking-tight text-[#111827] break-words">
                  {stat.value}
                </p>

                {/* Label */}
                <p className="mt-1 font-jakarta text-[11.5px] sm:text-[13px] font-medium text-[#556369] leading-tight">
                  {stat.label}
                </p>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}

export default AboutHero;
